import AuditDetail from '@app/components/Modal/AuditModal/AuditDetail';
import {useFetchAuditsQuery} from '@app/redux/services/auditService';
import {TAudit, AuditType} from '@app/types';
import formatDate from '@app/utils/formatDate';
import {Col, DatePicker, Divider, Form, Input, Row, Table, Tag} from 'antd';
import type {GetProps} from 'antd';
import dayjs from 'dayjs';
import {FC, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';
import useAuditHistory from './useAuditHistory';
import {LogEvents, useSendEvent} from '@app/utils/logger';
import {CONSTANTS} from '@app/constants';

type RangePickerProps = GetProps<typeof DatePicker.RangePicker>;

const AuditHistory: FC = () => {
  const {RangePicker} = DatePicker;

  const dateFormat = 'YYYY年MM月DD日';

  const {t} = useTranslation();
  const sendEvent = useSendEvent();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchDates, setSearchDates] = useState<RangePickerProps['value']>(
    () => {
      const startDate = searchParams.get('created_at:gte')
        ? dayjs(searchParams.get('created_at:gte'))
        : null;
      const endDate = searchParams.get('created_at:lte')
        ? dayjs(searchParams.get('created_at:lte'))
        : null;

      if (!startDate && !endDate) {
        return null;
      }

      return [startDate, endDate];
    },
  );
  const [searchText, setSearchText] = useState<string | undefined>(
    searchParams.get('search_keyword:like')
      ? String(searchParams.get('search_keyword:like'))
      : undefined,
  );

  const [state, actions] = useAuditHistory();

  const resetPaginationParams = () => {
    searchParams.delete('page');
    searchParams.delete('limit');
  };

  const onSearch = () => {
    sendEvent(LogEvents.AUDIT_SEARCH_PRESS, {text: searchText});
    if (searchText) {
      searchParams.set('search_keyword:like', searchText);
    } else {
      searchParams.delete('search_keyword:like');
    }
    resetPaginationParams();
    setSearchParams(searchParams);
  };

  const {data: auditsList, isLoading} = useFetchAuditsQuery({
    limit: searchParams.get('limit')
      ? Number(searchParams.get('limit'))
      : CONSTANTS.DEFAULT_PAGINATION_LIMIT,
    page: searchParams.get('page')
      ? Number(searchParams.get('page'))
      : undefined,
    'search_keyword:like': searchParams.get('search_keyword:like')
      ? String(searchParams.get('search_keyword:like'))
      : undefined,
    'created_at:gte': searchParams.get('created_at:gte')
      ? String(searchParams.get('created_at:gte'))
      : undefined,
    'created_at:lte': searchParams.get('created_at:lte')
      ? String(searchParams.get('created_at:lte'))
      : undefined,
  });

  const columns = [
    {
      title: t('Patient name'),
      dataIndex: 'patient_name',
      key: 'patient_name',
      sorter: true,
      width: '25%',
      textWrap: 'word-break',
      render: (value: string) => <b style={{color: '#0665ac'}}>{value}</b>,
    },
    {
      title: t('Date'),
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      width: '15%',
      render: (value: string) => (
        <div>{formatDate({date: value, format: 'dateOnly'})}</div>
      ),
    },
    {
      title: t('Reception number'),
      dataIndex: 'reception_number',
      key: 'reception_number',
      sorter: true,
      width: '10%',
      render: (value: string) => <Tag className="blue-tag">{value}</Tag>,
    },
    {
      title: t('Audit type'),
      dataIndex: 'mode',
      key: 'mode',
      sorter: true,
      width: '10%',
      render: (value: string) => {
        if (value === AuditType.quantity) {
          return <Tag className="blue-tag">{t('Quantity inspection')}</Tag>;
        } else if (value === AuditType.barcode) {
          return <Tag className="orange-tag">{t('Item inspection')}</Tag>;
        } else {
          return null;
        }
      },
    },
    {
      title: t('Auditor name'),
      dataIndex: 'auditor_name',
      key: 'auditor_name',
      sorter: true,
      width: '15%',
      textWrap: 'word-break',
    },
    {
      title: t('Medical facility name'),
      dataIndex: 'clinic_name',
      sorter: true,
      width: '25%',
      textWrap: 'word-break',
      render: (value: string, record: TAudit) => (
        <div>
          {value} {record.department_name}
        </div>
      ),
    },
  ];

  const onOpenChange = (open: boolean) => {
    if (!open) {
      const startDate = searchParams.get('created_at:gte');
      const endDate = searchParams.get('created_at:lte');
      sendEvent(LogEvents.AUDIT_DATE_RANGE_SELECT, {
        startDate: startDate,
        endDate: endDate,
      });
      resetPaginationParams();
      setSearchParams(searchParams);
    }
  };

  const onCalendarChange = (dates: RangePickerProps['value']) => {
    const newSearchParams = new URLSearchParams(searchParams);

    setSearchDates(dates);

    if (!dates) {
      newSearchParams.delete('created_at:gte');
      newSearchParams.delete('created_at:lte');
    } else {
      const [startDate, endDate] = dates;
      if (startDate) {
        newSearchParams.set('created_at:gte', startDate.format('YYYY-MM-DD'));
      } else {
        newSearchParams.delete('created_at:gte');
      }
      if (endDate) {
        newSearchParams.set('created_at:lte', endDate.format('YYYY-MM-DD'));
      } else {
        newSearchParams.delete('created_at:lte');
      }
    }

    setSearchParams(newSearchParams);
  };

  return (
    <>
      <AuditDetail
        auditId={state.selectedAuditId}
        open={state.isDetailModalOpen}
        onClose={actions.onDetailModalClose}
      />
      <Row gutter={16} style={{display: 'flex', alignItems: 'center'}}>
        <Col span={24} md={10} style={{marginBottom: 4, marginTop: 4}}>
          <Form colon={false}>
            <Form.Item label={t('Prescription Received Date')}>
              <RangePicker
                value={searchDates}
                onOpenChange={onOpenChange}
                onCalendarChange={onCalendarChange}
                placeholder={[t('Start from'), t('End to')]}
                format={dateFormat}
                style={{
                  display: 'flex',
                  width: '100%',
                }}
              />
            </Form.Item>
          </Form>
        </Col>
        <Col span={24} md={10} style={{marginBottom: 4, marginTop: 4}}>
          <Form.Item>
            <Input.Search
              onSearch={onSearch}
              onChange={(e) => {
                setSearchText(e.target.value);
              }}
              value={searchText}
              placeholder={t('Search')}
              enterButton
            />
          </Form.Item>
        </Col>
      </Row>
      <Divider />
      <Table
        columns={columns}
        dataSource={auditsList?.data}
        onRow={(row) => {
          return {
            onClick: () => {
              sendEvent(LogEvents.AUDIT_DETAIL_PRESS);
              actions.onDetailModalOpen(row.id);
            },
          };
        }}
        pagination={{
          total: auditsList?.meta.total_items,
          pageSize: auditsList?.meta.items_per_page,
          current: Number(searchParams.get('page') ?? '1'),
          position: ['bottomRight'],
          hideOnSinglePage: false,
        }}
        sticky={false}
        loading={isLoading}
        rowKey={(item) => item.id}
        onChange={actions.onTableChange}
        locale={{
          emptyText: (
            <div style={{textAlign: 'left'}}>
              {t('There is currently no audit history available for display.')}
            </div>
          ),
        }}
      />
    </>
  );
};

export default AuditHistory;
